import React, { useContext, useEffect, useState } from 'react';
import { EyeIcon, PencilIcon, EllipsisVerticalIcon } from '@heroicons/react/16/solid';
import { Dropdown, DropdownButton, DropdownItem, DropdownMenu } from '../../../components/Dropdown';
import { Badge } from '../../../components/Badge';
import { Button } from '../../../components/Button';
import { Heading } from '../../../components/Heading';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/Table';
import Pagination from '../../../components/Pagination';
import { Card } from '../../../components/Card';
import dateFormat from 'dateformat';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogActions, DialogDescription, DialogTitle } from '../../../components/Dialog';
import { useTrackingOrder } from '../../../hooks/useTrackingOrder';
import { TrackingOrder } from '../../../models/TrackingOrder';
import { UserContext } from '../../../context/userContext';
import { Text } from '../../../components/Text';
import { useWarehouses } from '../../../hooks/useWarehouse';
import Loading from '../../../components/Loading';
import { ListToolbarRow, ListToolbarSearch, FiltersButton, SortMenu, ColumnsMenu, isColumnVisible } from '../../../components/ListToolbar';
import { FiltersDialog, FilterPills, FilterPill } from '../../../components/FiltersDialog';
import { DateRangePicker } from '../../../components/DateRangePicker';
import { Select } from '../../../components/Select';
import { ArrowPathIcon } from '@heroicons/react/16/solid';
import SqlAccountDoQrSyncService from '../../../services/sqlAccountDoQrSyncService';
import showSuccessToast from '../../../components/Toasts/ShowSuccessToast';
import showErrorToast from '../../../components/Toasts/ShowErrorToast';

const COLUMNS = [
    { key: 'reference', label: 'Reference No' },
    { key: 'delivery', label: 'Delivery Number' },
    { key: 'qr', label: 'SQL Account QR' },
    { key: 'customer', label: 'Customer' },
    { key: 'status', label: 'Status' },
    { key: 'date', label: 'Date' },
];
const SORT_OPTIONS = [
    { label: 'Newest first', value: 'newest' },
    { label: 'Oldest first', value: 'oldest' },
];

const TrackingStandalonePage: React.FC = () => {
    const { currentUser } = useContext(UserContext);
    const { trackingOrders, loading, error, fetchTrackingOrders, deleteTrackingOrder } = useTrackingOrder();
    const { warehouses } = useWarehouses();
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [filterStartDate, setFilterStartDate] = useState('');
    const [filterEndDate, setFilterEndDate] = useState('');
    const [filterYear, setFilterYear] = useState('');
    const [filterWarehouseId, setFilterWarehouseId] = useState('');
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [syncing, setSyncing] = useState(false);
    const [sortOrder, setSortOrder] = useState('newest');
    const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({});
    const toggleColumn = (key: string) => setColumnVisibility((prev) => ({ ...prev, [key]: prev[key] === false ? true : false }));
    const perPage = 10;
    const navigate = useNavigate();

    // The DO's actual date from SQL Account when available, falling back to when our system's
    // row was created (e.g. for manually-created orders, or ones with no backing SQL Account DO).
    const getOrderDate = (order: TrackingOrder) => order.document_date || order.created_time;

    const resetFilters = () => {
        setFilterStartDate('');
        setFilterEndDate('');
        setFilterYear('');
        setFilterWarehouseId('');
    };

    const availableYears = Array.from(
        new Set(
            (Array.isArray(trackingOrders) ? trackingOrders : [])
                .map((o) => new Date(getOrderDate(o)).getFullYear())
                .filter((year) => !Number.isNaN(year))
        )
    ).sort((a, b) => b - a);

    const activeFilterCount = [filterStartDate, filterEndDate, filterYear, filterWarehouseId].filter(Boolean).length;

    const filterPills: FilterPill[] = [
        ...(filterStartDate ? [{ key: 'from', label: `From: ${filterStartDate}`, onClear: () => setFilterStartDate('') }] : []),
        ...(filterEndDate ? [{ key: 'to', label: `To: ${filterEndDate}`, onClear: () => setFilterEndDate('') }] : []),
        ...(filterYear ? [{ key: 'year', label: `Year: ${filterYear}`, onClear: () => setFilterYear('') }] : []),
        ...(filterWarehouseId ? [{
            key: 'warehouse',
            label: `Warehouse: ${warehouses?.find((w) => w.id?.toString() === filterWarehouseId)?.warehouse_name || filterWarehouseId}`,
            onClear: () => setFilterWarehouseId(''),
        }] : []),
    ];

    const filteredOrders = Array.isArray(trackingOrders)
        ? trackingOrders.filter((order) => {
            const matchesSearch = order.reference_no?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                order.customer_fullname?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                order.delivery_number?.toLowerCase().includes(searchQuery.toLowerCase());
            const orderDate = getOrderDate(order);
            const matchesStartDate = !filterStartDate || new Date(orderDate) >= new Date(filterStartDate);
            const matchesEndDate = !filterEndDate || new Date(orderDate) <= new Date(filterEndDate);
            const matchesYear = !filterYear || new Date(orderDate).getFullYear().toString() === filterYear;
            const matchesWarehouse = !filterWarehouseId || order.warehouse_id === Number(filterWarehouseId);
            return matchesSearch && matchesStartDate && matchesEndDate && matchesYear && matchesWarehouse;
        }) : [];

    const sortedOrders = [...filteredOrders].sort((a, b) => {
        const diff = new Date(getOrderDate(a)).getTime() - new Date(getOrderDate(b)).getTime();
        return sortOrder === 'newest' ? -diff : diff;
    });
    const totalPages = Math.max(1, Math.ceil(filteredOrders.length / perPage));
    const paginatedOrders = sortedOrders.slice((currentPage - 1) * perPage, currentPage * perPage);

    useEffect(() => {
        document.title = "Tracking Orders";
    }, []);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery, filterStartDate, filterEndDate, filterYear, filterWarehouseId]);

    const handleSyncNow = async () => {
        setSyncing(true);
        try {
            const { results } = await SqlAccountDoQrSyncService.syncToday();
            const totalProcessed = results.reduce((sum, r) => sum + (r.processed?.length || 0), 0);
            const failed = results.filter((r) => r.error || (r.errors && r.errors.length > 0));
            if (failed.length > 0) {
                showErrorToast(`Synced with issues: ${totalProcessed} DO(s) updated, ${failed.length} warehouse(s) had errors.`);
            } else {
                showSuccessToast(`Sync complete: ${totalProcessed} DO(s) updated from SQL Account.`);
            }
            fetchTrackingOrders();
        } catch (err: any) {
            showErrorToast(err?.response?.data?.message || 'Failed to sync from SQL Account');
        } finally {
            setSyncing(false);
        }
    };

    const handleDelete = async () => {
        if (!selectedOrderId) return;
        setIsDeleting(true);
        try {
            await deleteTrackingOrder(selectedOrderId);
            setIsDeleteOpen(false);
            setSelectedOrderId(null);
        } catch (e) {
            console.error(e);
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <>
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div className="max-sm:w-full sm:flex-1">
                    <Heading>Tracking Orders</Heading>
                </div>
                <div className="flex flex-wrap gap-3">
                    {currentUser?.role?.toString() === '1' && (
                        <Button outline onClick={handleSyncNow} disabled={syncing}>
                            <ArrowPathIcon className={syncing ? 'animate-spin' : undefined} />
                            {syncing ? 'Syncing…' : 'Sync from SQL Account'}
                        </Button>
                    )}
                    <Button onClick={() => navigate('/gp/tracking/standalone/add')}>Create tracking order</Button>
                </div>
            </div>

            <Card className="mt-6 p-5">
            <ListToolbarRow search={<ListToolbarSearch value={searchQuery} onChange={setSearchQuery} placeholder="Search orders…" />}>
                <FiltersButton count={activeFilterCount} onClick={() => setIsFilterOpen(true)} />
                <SortMenu options={SORT_OPTIONS} value={sortOrder} onChange={setSortOrder} />
                <ColumnsMenu columns={COLUMNS} visible={columnVisibility} onToggle={toggleColumn} />
            </ListToolbarRow>
            <FilterPills pills={filterPills} onClearAll={resetFilters} />

            <ul className="mt-10">
                {loading && <div className="w-full text-center h-full py-36 justify-center"><Loading /></div>}
                {error && <p className="py-16 text-center text-red-700">{error}</p>}
                {(!loading && !error) && (
                    <div>
                        <Table className="[--gutter:theme(spacing.6)] lg:[--gutter:theme(spacing.10)]">
                            <TableHead>
                                <TableRow>
                                    <TableHeader>S/N</TableHeader>
                                    {isColumnVisible(columnVisibility, 'reference') && <TableHeader>Reference No</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'delivery') && <TableHeader>Delivery Number</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'qr') && <TableHeader>SQL Account QR</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'customer') && <TableHeader>Customer</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'status') && <TableHeader>Status</TableHeader>}
                                    {isColumnVisible(columnVisibility, 'date') && <TableHeader>Date</TableHeader>}
                                    <TableHeader className="text-center">Actions</TableHeader>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {paginatedOrders.length > 0 ? paginatedOrders.map((order, index) => (
                                    <TableRow key={order.id} title={order.reference_no}>
                                        <TableCell>{(currentPage - 1) * perPage + index + 1}</TableCell>
                                        {isColumnVisible(columnVisibility, 'reference') && <TableCell>{order.reference_no}</TableCell>}
                                        {isColumnVisible(columnVisibility, 'delivery') && <TableCell>{order.delivery_number || '-'}</TableCell>}
                                        {isColumnVisible(columnVisibility, 'qr') && <TableCell>
                                            {!order.delivery_number ? (
                                                <span className="text-zinc-400">-</span>
                                            ) : order.sql_account_qr_synced_at ? (
                                                <Badge color="lime" title={dateFormat(order.sql_account_qr_synced_at, "mmmm dd, yyyy HH:MM")}>Synced</Badge>
                                            ) : (
                                                <Badge color="amber">Pending</Badge>
                                            )}
                                        </TableCell>}
                                        {isColumnVisible(columnVisibility, 'customer') && <TableCell>{order.customer_fullname}</TableCell>}
                                        {isColumnVisible(columnVisibility, 'status') && <TableCell className="text-zinc-500">{order.status}</TableCell>}
                                        {isColumnVisible(columnVisibility, 'date') && <TableCell className="text-zinc-500">{dateFormat(getOrderDate(order), "mmmm dd, yyyy")}</TableCell>}
                                        <TableCell className="text-center flex gap-4 justify-center">
                                            <a onClick={() => navigate(`/gp/tracking/standalone/view/${order.id}`)} className="text-zinc-600 hover:text-zinc-900 cursor-pointer">
                                                <EyeIcon className="h-5 w-5" />
                                            </a>
                                            <a onClick={() => navigate(`/gp/tracking/standalone/edit/${order.id}`)} className="text-zinc-600 hover:text-zinc-900 cursor-pointer">
                                                <PencilIcon className="h-5 w-5" />
                                            </a>
                                            <Dropdown>
                                                <DropdownButton plain aria-label="More options" className="h-5 w-5">
                                                    <EllipsisVerticalIcon />
                                                </DropdownButton>
                                                <DropdownMenu anchor="bottom end">
                                                    <DropdownItem onClick={() => { setSelectedOrderId(order.id); setIsDeleteOpen(true); }}>Delete</DropdownItem>
                                                </DropdownMenu>
                                            </Dropdown>
                                        </TableCell>
                                    </TableRow>
                                )) : (
                                    <TableRow>
                                        <TableCell colSpan={8} className="text-center py-8">No tracking orders found.</TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                        {filteredOrders.length > 0 && (
                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={setCurrentPage}
                                totalResult={filteredOrders.length}
                                dataName="orders"
                                pageSize={perPage}
                            />
                        )}
                    </div>
                )}
            </ul>
            </Card>

            <FiltersDialog open={isFilterOpen} onClose={() => setIsFilterOpen(false)} onReset={resetFilters}>
                <div className="sm:col-span-2">
                    <Text>Date Range</Text>
                    <DateRangePicker
                        from={filterStartDate}
                        to={filterEndDate}
                        onChange={({ from, to }) => { setFilterStartDate(from); setFilterEndDate(to); }}
                        className="w-full"
                    />
                </div>
                <div>
                    <Text>By Year</Text>
                    <Select value={filterYear} onChange={(e) => setFilterYear(e.target.value)}>
                        <option value="">All Years</option>
                        {availableYears.map((year) => (
                            <option key={year} value={year.toString()}>{year}</option>
                        ))}
                    </Select>
                </div>
                {(currentUser?.role?.toString() === "1") && (
                    <div>
                        <Text>By Warehouse</Text>
                        <Select value={filterWarehouseId} onChange={(e) => setFilterWarehouseId(e.target.value)}>
                            <option value="">All Warehouses</option>
                            {warehouses?.map((w) => (
                                <option key={w.id} value={w.id?.toString()}>
                                    {w.warehouse_name}
                                </option>
                            ))}
                        </Select>
                    </div>
                )}
            </FiltersDialog>

            <Dialog open={isDeleteOpen} onClose={() => setIsDeleteOpen(false)}>
                <DialogTitle>Delete tracking order</DialogTitle>
                <DialogDescription>Are you sure you want to delete this tracking order? This action cannot be undone.</DialogDescription>
                <DialogActions>
                    <Button plain onClick={() => setIsDeleteOpen(false)} disabled={isDeleting}>Cancel</Button>
                    <Button onClick={handleDelete} disabled={isDeleting}>{isDeleting ? <Loading /> : 'Delete'}</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default TrackingStandalonePage;
